import { FC, useEffect, useState } from "react";
import {
  Flex,
  FlexProps,
  FormControl,
  FormErrorMessage,
  FormLabel,
  useRadioGroup,
} from "@chakra-ui/react";
import { useField, useFormikContext } from "formik";
import { RadioCard } from "~/OnboardingApp/components/inputs/RadioCard";

interface Props {
  autoAdvance?: boolean | ((option: string) => boolean);
  defaultValue?: string;
  label?: string;
  labelVariant?: string;
  name: string;
  options: Array<{
    label: string;
    value: string;
  }>;
  radioGroupProps?: FlexProps;
}

export const MilestoneSelectInput: FC<Props> = ({
  autoAdvance,
  defaultValue,
  label,
  name,
  options,
  radioGroupProps,
}) => {
  const [field, meta] = useField<string>(name);
  const { submitForm } = useFormikContext<Record<string, unknown>>();
  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    defaultValue: defaultValue ?? "",
  });
  const [interacted, setInteracted] = useState(false);
  const [shouldAdvance, setShouldAdvance] = useState(false);

  const { value } = field;

  useEffect(() => {
    if (!interacted) return;

    if (typeof autoAdvance === "function") {
      autoAdvance(value) && setShouldAdvance(true);
    } else if (autoAdvance) {
      setShouldAdvance(true);
    }
  }, [autoAdvance, value, interacted]);

  useEffect(() => {
    if (shouldAdvance) {
      submitForm();
    }
  }, [shouldAdvance, submitForm]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    field.onChange(e);
    setInteracted(true);
  };
  const group = getRootProps();

  return (
    <FormControl isInvalid={meta.touched && !!meta.error}>
      {label && <FormLabel htmlFor={name}>{label}</FormLabel>}

      <Flex
        direction="column"
        id={name}
        w="full"
        {...radioGroupProps}
        {...field}
        {...group}
        onChange={handleChange}
        gap={2}
      >
        {options.map((option) => {
          const radio = getRadioProps({ value: option.value });
          return <RadioCard key={option.value} label={option.label} {...radio} />;
        })}
      </Flex>

      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
};
